import { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";
// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TechniciansWashoutContent from "./TechniciansWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";
// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

const TechniciansWashoutContainer = (props) => {
	const {
		position = "",
		positionWithoutSpaces = "",
		subscriptionsInformation = {},
		actions = {},
		selected = null,
		location = null,
		updateSidePanelCallBack,
		activeCommissionCycles = [],
	} = props;

	const queryClient = useQueryClient();

	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries = useWashoutQueries(position);

	const { getSubscription } = useSubscriptions();
	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actions?.OVERRIDE_COMPENSATION);

	const { data: model, isLoading } = useQuery({
		queryKey: ["getWashout", selected?.payPeriodId, position, selected?.userId, selected?.commissionCycleId],
		queryFn: async () => {
			const { locationId } = location || {};
			const { userId, commissionCycleId, payPeriodId, position: selectedPosition } = selected || {};
			return washoutQueries?.getWashout(locationId, userId, commissionCycleId, payPeriodId, selectedPosition);
		},

		enabled: !!location && !!selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: location?.locationId,
		commissionCycleId: selected?.commissionCycleId,
		payPeriodId: selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: location?.locationId,
		commissionCycleId: selected?.commissionCycleId,
		payPeriodId: selected?.payPeriodId,
	});

	const handleSummaryChange = (input) => {
		if (!input || !location || !model) {
			return;
		}
		recalculate({
			input,
			position: model?.employee?.position || position,
			isDepartmental: false,
			locationId: location.locationId,
			washoutId: model._id,
		});
	};

	const prevKeyRef = useRef("");
	const subsPayload = useMemo(
		() => ({
			locationId: location?.locationId,
			userId: selected?.userId,
			payPeriodId: selected?.payPeriodId,
			commissionCycleId: selected?.commissionCycleId,
			position,
			positionWithoutSpaces,
			activeCommissionCycles,
		}),
		[
			location?.locationId,
			selected?.userId,
			selected?.payPeriodId,
			selected?.commissionCycleId,
			position,
			positionWithoutSpaces,
			activeCommissionCycles,
		]
	);

	useEffect(() => {
		if (!subsPayload.locationId || !subsPayload.userId) {
			return;
		}

		const key = JSON.stringify(subsPayload);
		if (prevKeyRef.current === key) {
			return;
		}
		prevKeyRef.current = key;

		handleWashoutSubscriptions({
			subscriptionsInformation,
			props: {
				location,
				selected,
				positionWithoutSpaces: subsPayload.positionWithoutSpaces,
				activeCommissionCycles: subsPayload.activeCommissionCycles,
			},
			actions: {
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: true,
			},
		});

		updateSidePanelCallBack?.(selected);
	}, [subsPayload, subscriptionsInformation, selected, location, queryClient, getSubscription, updateSidePanelCallBack]);

	return (
		<>
			<TechniciansWashoutContent
				model={model}
				locationId={location?.locationId || ""}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
				positionWithoutSpaces={positionWithoutSpaces}
			/>
			{!model && !isLoading && (
				<RoundedBox>
					<EmptyBody
						image={selected ? emptyTable : empty}
						title={selected ? "No washout found for this technician" : `Select a ${position} to view their washout`}
						bodyHeight={"510px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				</RoundedBox>
			)}

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</>
	);
};

TechniciansWashoutContainer.propTypes = {
	position: PropTypes.string,
	positionWithoutSpaces: PropTypes.string,
	subscriptionsInformation: PropTypes.object,
	selected: PropTypes.shape({
		userId: PropTypes.string,
		commissionCycleId: PropTypes.string,
		payPeriodId: PropTypes.string,
		status: PropTypes.string,
	}),
	actions: PropTypes.shape({
		OVERRIDE_COMPENSATION: PropTypes.any,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
	}),
	activeCommissionCycles: PropTypes.array,
	updateSidePanelCallBack: PropTypes.func,
};

export default TechniciansWashoutContainer;
